/**
 * OrganizationBilling - Gestione Fatturazione Organization
 * Il Diario di Bordo - SaaS
 * 
 * FASE 5: Pagina per gestire la fatturazione
 * - Visualizza piano corrente
 * - Storico fatture
 * - Metodi di pagamento
 * - Link a Stripe Customer Portal
 * 
 * FIX: Aggiunta sezione Piani Disponibili con betaMode
 */

import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useNotification } from '../../contexts/NotificationContext'
import { useAuth } from '../../contexts/AuthContext'
import { organizationApi } from '../../services/organizationApi'
import api from '../../services/api'
import { Card, PageLoader, Badge, EmptyState } from '../../components/ui'

import {
  CreditCard,
  ArrowLeft,
  Download,
  ExternalLink,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Receipt,
  Loader2,
  TrendingUp,
  Building2,
  Euro,
  FileText,
  Users,
  UserCheck,
  Baby,
  Database,
  Sparkles,
  Check,
  X,
  Info,
  CloudCog,
  FileBarChart,
  ShoppingCart,
  ArrowRight,
  Send,
  XCircle,
  RotateCcw,
  AlertTriangle,
  Trash2
} from 'lucide-react'

// ============================================
// INVOICE STATUS BADGE
// ============================================

function InvoiceStatusBadge({ status }) {
  const config = {
    paid: { label: 'Pagata', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
    open: { label: 'In Attesa', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
    pending: { label: 'In Attesa', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
    overdue: { label: 'Scaduta', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: AlertCircle },
    draft: { label: 'Bozza', color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400', icon: FileText },
    void: { label: 'Annullata', color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400', icon: X },
    uncollectible: { label: 'Non riscossa', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: AlertCircle }
  }
  
  const { label, color, icon: Icon } = config[status] || config.pending
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${color}`}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  )
}

// ============================================
// PLAN CARD COMPONENT - NUOVO
// ============================================

const formatLimit = (value) => {
  if (value >= 999999 || value === 'Illimitati' || value === 'Illimitato') {
    return 'Illimitati'
  }
  return value
}

function AvailablePlanCard({ plan, betaMode, onSelect }) {
  const limits = plan.limits || {}
  const features = plan.features || {}
  const minori = limits.maxMinori || 0
  const educatori = limits.maxEducatori || 0
  const coordinatori = limits.maxCoordinatori || 0
  const storico = limits.storageMonths || 0

  // Features per funzionalità incluse
  const hasBackupGDrive = features.backupGoogleDrive !== false
  const hasReportBase = features.reportsBasic !== false
  const hasReportAdvanced = features.reportsAdvanced === true

  return (
    <Card className={`relative ${plan.recommended ? 'border-primary-500 ring-2 ring-primary-500' : ''}`}>
      {plan.recommended && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
          Consigliato
        </Badge>
      )}
      
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
          {plan.name}
        </h3>
        
        {/* Prezzo - blur solo se betaMode è attivo */}
        <div className="mt-3 relative">
          <p className={betaMode ? "blur-lg select-none pointer-events-none" : ""}>
            <span className="text-3xl font-bold">
              {plan.pricing?.monthly?.formatted || '€??'}
            </span>
            <span className="text-gray-500">/mese</span>
          </p>
        </div>
        
        {/* Badge Beta Tester - solo se betaMode è attivo */}
        {betaMode && (
          <div className="mt-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-300 px-4 py-2 rounded-full text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            🎉 Accesso gratuito durante la beta
          </div>
        )}
      </div>

      {/* Limiti Piano */}
      <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
        <p className="text-xs font-medium text-primary-700 dark:text-primary-300 mb-2">Limiti inclusi:</p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1">
            <Baby className="w-3 h-3 text-primary-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {formatLimit(minori)} minori
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-3 h-3 text-primary-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {formatLimit(educatori)} educatori
            </span>
          </div>
          <div className="flex items-center gap-1">
            <UserCheck className="w-3 h-3 text-primary-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {formatLimit(coordinatori)} coord.
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Database className="w-3 h-3 text-primary-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {storico >= 999 ? 'Illimitato' : `${storico} mesi`}
            </span>
          </div>
        </div>
      </div>

      {/* Funzionalità incluse */}
      <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600">
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Funzionalità incluse:</p>
        <div className="space-y-1.5 text-sm">
          <div className={`flex items-center gap-1.5 ${hasBackupGDrive ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
            {hasBackupGDrive ? <Check className="w-3 h-3 text-green-500" /> : <X className="w-3 h-3 text-gray-400" />}
            <CloudCog className="w-3 h-3" />
            <span>Backup Google Drive</span>
          </div>
          <div className={`flex items-center gap-1.5 ${hasReportBase ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
            {hasReportBase ? <Check className="w-3 h-3 text-green-500" /> : <X className="w-3 h-3 text-gray-400" />}
            <FileBarChart className="w-3 h-3" />
            <span>Report Base</span>
          </div>
          <div className={`flex items-center gap-1.5 ${hasReportAdvanced ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
            {hasReportAdvanced ? <Check className="w-3 h-3 text-green-500" /> : <X className="w-3 h-3 text-gray-400" />}
            <FileBarChart className="w-3 h-3" />
            <span>Report Avanzati</span>
          </div>
        </div>
      </div>

      {/* Personalizzazione Form */}
      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
        <p className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          Personalizzazione Form:
        </p>
        <div className="space-y-1.5 text-sm">
          {plan.id === 'starter' ? (
            <>
              <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                <X className="w-3 h-3 text-gray-400" />
                <span>Personalizza opzioni campi</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                <X className="w-3 h-3 text-gray-400" />
                <span>Sezioni personalizzate</span>
              </div>
            </>
          ) : plan.id === 'professional' ? (
            <>
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>Personalizza opzioni (max 5)</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>Disabilita campi illimitati</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                <X className="w-3 h-3 text-gray-400" />
                <span>Sezioni personalizzate</span>
              </div>
            </>
          ) : plan.id === 'enterprise' ? (
            <>
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>Personalizza opzioni (max 5)</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>Disabilita campi illimitati</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>3 sezioni personalizzate (max 5 elem.)</span>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Bottone Attiva */}
      {!betaMode && onSelect && (
        <button
          onClick={() => onSelect(plan)}
          className={`w-full mt-4 py-2.5 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
            plan.recommended
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          <ShoppingCart className="w-4 h-4" />
          Attiva Piano
        </button>
      )}
    </Card>
  )
}

// ============================================
// ============================================
// BILLING SUMMARY CARD - Riepilogo Organization
// ============================================

function BillingSummaryCard({ billing, onManage, loading }) {
  if (!billing || !billing.subscriptions || billing.subscriptions.length === 0) {
    return (
      <Card>
        <div className="text-center py-8">
          <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Nessun abbonamento attivo
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Crea un centro per iniziare
          </p>
        </div>
      </Card>
    )
  }
  
  const activeSubs = billing.subscriptions.filter(s => s.status === 'active')
  const trialSubs = billing.subscriptions.filter(s => s.status === 'trialing')
  
  return (
    <Card className="border-2 border-primary-200 dark:border-primary-700">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Riepilogo Abbonamenti
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {billing.centersCount} {billing.centersCount === 1 ? 'centro' : 'centri'} nell'organizzazione
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            €{billing.totalMonthly?.toFixed(2) || '0.00'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">/mese totale</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {billing.centersCount || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Centri Totali</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {activeSubs.length}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Attivi</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {trialSubs.length}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">In Prova</p>
        </div>
      </div>
      
      {billing.stripeCustomerId && (
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onManage}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4 mr-2" />
            )}
            Gestisci su Stripe
          </button>
        </div>
      )}
    </Card>
  )
}

// ============================================
// CENTER SUBSCRIPTION CARD - Per singolo centro
// ============================================

const subStatusConfig = {
  active: { label: 'Attivo', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
  trialing: { label: 'Prova Gratuita', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock },
  past_due: { label: 'Pagamento in Ritardo', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: AlertCircle },
  canceled: { label: 'Cancellato', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: AlertCircle }
}

const planColors = {
  trial: 'border-l-blue-400',
  starter: 'border-l-green-400',
  professional: 'border-l-purple-400',
  enterprise: 'border-l-amber-400'
}

function CenterSubscriptionCard({ sub, canManage, onCancel, onReactivate, onCancelDeletion, actionLoading }) {
  const statusCfg = subStatusConfig[sub.status] || subStatusConfig.trialing
  const StatusIcon = statusCfg.icon

  const intervalLabel = sub.billingInterval === 'yearly'
    ? 'anno'
    : sub.billingInterval === 'monthly'
      ? 'mese'
      : null

  // Il centro è in cancellazione programmata (workflow 30+90gg post-Stripe)
  const isPendingDeletion = sub.centerStatus === 'pending_deletion'

  // Un abbonamento è "cancellabile" se è attivo, non in trial, non già in cancellazione, non già canceled
  const canCancel = canManage
    && sub.status === 'active'
    && !sub.cancelAtPeriodEnd
    && sub.plan !== 'trial'
    && !isPendingDeletion

  // È riattivabile se è in cancel_at_period_end ma il periodo non è ancora scaduto
  const canReactivate = canManage
    && sub.cancelAtPeriodEnd
    && sub.currentPeriodEnd
    && new Date(sub.currentPeriodEnd) > new Date()
    && !isPendingDeletion

  // Centro in cancellazione: il responsabile può annullarla finché non scade
  const canCancelDeletion = canManage && isPendingDeletion

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 ${isPendingDeletion ? 'border-l-red-400' : (planColors[sub.plan] || planColors.trial)} p-5`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <Building2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white">
              {sub.centerName}
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
              Piano {sub.plan}
            </p>
          </div>
        </div>
        {isPendingDeletion ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <Trash2 className="h-3.5 w-3.5" />
            In cancellazione
          </span>
        ) : (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${statusCfg.color}`}>
            <StatusIcon className="h-3.5 w-3.5" />
            {statusCfg.label}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-4">
          {sub.amount > 0 && intervalLabel && (
            <div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                €{(sub.finalPrice / 100).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">/{intervalLabel}</p>
            </div>
          )}
          {sub.amount === 0 && (
            <div>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">Gratuito</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {sub.status === 'trialing' ? 'Periodo di prova' : 'Piano trial'}
              </p>
            </div>
          )}
        </div>

        <div className="text-right text-sm">
          {sub.currentPeriodEnd && (
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {sub.cancelAtPeriodEnd ? 'Termina' : 'Rinnovo'}{' '}
                {new Date(sub.currentPeriodEnd).toLocaleDateString('it-IT')}
              </span>
            </div>
          )}
          {sub.daysRemaining != null && sub.status === 'trialing' && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              {sub.daysRemaining} giorni rimanenti
            </p>
          )}
        </div>
      </div>

      {/* Banner cancellazione pianificata */}
      {sub.cancelAtPeriodEnd && sub.currentPeriodEnd && !isPendingDeletion && (
        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-200">
            <p className="font-medium">Abbonamento in cancellazione</p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
              Il servizio resterà attivo fino al {new Date(sub.currentPeriodEnd).toLocaleDateString('it-IT')}. Puoi riattivarlo in qualsiasi momento prima di questa data.
            </p>
          </div>
        </div>
      )}

      {/* Banner centro in cancellazione (post-scadenza Stripe) */}
      {isPendingDeletion && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800 dark:text-red-200">
            <p className="font-medium">Centro in cancellazione</p>
            <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
              L'abbonamento è scaduto e il centro verrà eliminato
              {sub.scheduledDeletionAt && (
                <> il <strong>{new Date(sub.scheduledDeletionAt).toLocaleDateString('it-IT')}</strong></>
              )}.
              Annulla la cancellazione per poter riattivare un piano.
            </p>
          </div>
        </div>
      )}

      {/* Azioni cancel / reactivate / cancel-deletion */}
      {(canCancel || canReactivate || canCancelDeletion) && (
        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
          {canCancel && (
            <button
              type="button"
              onClick={() => onCancel?.(sub)}
              disabled={actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle className="h-4 w-4" />
              Interrompi abbonamento
            </button>
          )}
          {canReactivate && (
            <button
              type="button"
              onClick={() => onReactivate?.(sub)}
              disabled={actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Riattiva abbonamento
            </button>
          )}
          {canCancelDeletion && (
            <button
              type="button"
              onClick={() => onCancelDeletion?.(sub)}
              disabled={actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Annulla cancellazione
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================
// CANCEL SUBSCRIPTION MODAL
// ============================================

function CancelSubscriptionModal({ sub, onConfirm, onClose, loading }) {
  const [reason, setReason] = useState('')
  const MAX = 500

  const activeUntilStr = sub.currentPeriodEnd
    ? new Date(sub.currentPeriodEnd).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={loading ? undefined : onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Interrompi l'abbonamento?
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {sub.centerName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Info box */}
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            L'abbonamento resterà <strong>attivo fino al {activeUntilStr}</strong>.
            Dopo quella data il centro verrà disattivato. Puoi riattivare in qualsiasi
            momento prima della scadenza senza costi aggiuntivi e senza perdere dati.
          </p>
        </div>

        {/* Reason textarea */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Motivo <span className="text-gray-400 font-normal">(opzionale, ci aiuta a migliorare)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, MAX))}
            placeholder="es. Il centro ha cessato le attività, cambio piano, costi, ecc."
            rows={3}
            disabled={loading}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none disabled:opacity-50"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{reason.length}/{MAX}</p>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Il responsabile e i coordinatori del centro riceveranno una email di conferma.
        </p>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            onClick={() => onConfirm(sub, reason.trim() || undefined)}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
            Conferma interruzione
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================
// INVOICE ROW
// ============================================

function InvoiceRow({ invoice, onResend }) {
  const [downloading, setDownloading] = useState(false)
  const [resending, setResending] = useState(false)

  // Determina se è una fattura Stripe (ha id tipo "in_xxx") o locale (ha _id MongoDB)
  const isStripeInvoice = invoice.id && !invoice._id
  const invoiceId = invoice._id || invoice.id

  // Download PDF - usa URL diretto Stripe o fallback backend locale
  const handleDownloadPdf = async () => {
    // Se c'è un URL PDF diretto (Stripe), apri in nuova tab
    const pdfUrl = invoice.pdfUrl || invoice.invoice_pdf
    if (pdfUrl) {
      window.open(pdfUrl, '_blank')
      return
    }

    // Fallback: download dal backend locale (solo per fatture locali con _id)
    if (!invoice._id) {
      if (onResend) onResend('error', 'PDF non disponibile per questa fattura')
      return
    }

    try {
      setDownloading(true)
      const response = await api.get(`/invoices/${invoice._id}/download`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Fattura_${invoice.invoiceNumber || invoice.number || invoice._id}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download PDF error:', err)
      if (onResend) onResend('error', 'Errore nel download del PDF')
    } finally {
      setDownloading(false)
    }
  }

  // Reinvia email con PDF - solo per fatture locali con _id
  const handleResendEmail = async () => {
    if (!invoice._id) return
    try {
      setResending(true)
      await api.post(`/invoices/${invoice._id}/resend-email`)
      if (onResend) onResend('success', 'Email reinviata con successo')
    } catch (err) {
      console.error('Resend email error:', err)
      const msg = err.response?.data?.message || 'Errore nel reinvio dell\'email'
      if (onResend) onResend('error', msg)
    } finally {
      setResending(false)
    }
  }

  // Determina data da visualizzare
  // stripeService.getInvoices restituisce created come Date object
  // Le fatture locali hanno invoiceDate o createdAt come stringa ISO
  let invoiceDate
  if (invoice.invoiceDate) {
    invoiceDate = new Date(invoice.invoiceDate)
  } else if (invoice.created) {
    // Se created è già un Date o una stringa ISO, new Date() funziona
    // Se è un unix timestamp (numero < 2e10), moltiplica per 1000
    const created = invoice.created
    invoiceDate = (typeof created === 'number' && created < 2e10)
      ? new Date(created * 1000)
      : new Date(created)
  } else {
    invoiceDate = new Date(invoice.createdAt)
  }

  // Determina importo (in centesimi)
  // Priorità: total (importo fattura) > amountDue (da pagare) > amountPaid (incassato) > fallback
  const amount = invoice.total || invoice.amountDue || invoice.totalAmount || invoice.amount || invoice.amountPaid || invoice.amount_paid || 0

  // Determina se il PDF è disponibile
  const hasPdf = !!(invoice.pdfUrl || invoice.invoice_pdf || invoice._id)

  // Determina numero fattura
  const invoiceNumber = invoice.invoiceNumber || invoice.number || invoiceId?.slice?.(-8) || '—'

  return (
    <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-4">
        <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <Receipt className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </div>
        <div>
          <p className="font-medium text-gray-900 dark:text-white">
            Fattura #{invoiceNumber}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isNaN(invoiceDate.getTime())
              ? '—'
              : invoiceDate.toLocaleDateString('it-IT', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })
            }
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <div className="text-right">
          <p className="font-semibold text-gray-900 dark:text-white">
            €{(amount / 100).toFixed(2)}
          </p>
        </div>
        <InvoiceStatusBadge status={invoice.status} />
        
        {/* Download PDF */}
        {hasPdf && (
          <button
            onClick={handleDownloadPdf}
            disabled={downloading}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            title="Scarica PDF"
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </button>
        )}

        {/* Reinvia email - solo per fatture locali */}
        {!isStripeInvoice && invoice.status === 'paid' && (invoice.emailResendCount == null || invoice.emailResendCount < 3) && (
          <button
            onClick={handleResendEmail}
            disabled={resending}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
            title="Reinvia email con fattura"
          >
            {resending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================
// CHECKOUT MODAL
// ============================================

function CheckoutModal({ plan, centers, onConfirm, onUpgrade, onClose, loading }) {
  const [selectedCenter, setSelectedCenter] = useState('')
  const [selectedInterval, setSelectedInterval] = useState('monthly')
  const [couponCode, setCouponCode] = useState('')
  
  // Tutti i centri dell'organizzazione
  const allCenters = centers || []
  
  // Determina il tipo di azione per il centro selezionato
  const selectedCenterData = allCenters.find(c => c.centerId === selectedCenter)
  const isUpgrade = selectedCenterData && selectedCenterData.plan !== 'trial' && selectedCenterData.status !== 'trialing'
  const isSamePlan = selectedCenterData && selectedCenterData.plan === plan.id
  const isDowngrade = selectedCenterData && !isSamePlan && isUpgrade && 
    ['starter', 'professional', 'enterprise'].indexOf(selectedCenterData.plan) > ['starter', 'professional', 'enterprise'].indexOf(plan.id)

  // Label azione
  const getActionLabel = () => {
    if (!selectedCenterData) return ''
    if (isSamePlan) return 'Piano attuale'
    if (isDowngrade) return 'Downgrade'
    if (isUpgrade) return 'Upgrade'
    return 'Nuova attivazione'
  }

  // Descrizione azione
  const getActionDescription = () => {
    if (!selectedCenterData) return ''
    if (isSamePlan) return `Questo centro ha già il piano ${plan.name}.`
    if (isUpgrade) {
      return `Cambio da ${selectedCenterData.plan.charAt(0).toUpperCase() + selectedCenterData.plan.slice(1)} → ${plan.name}. La differenza verrà calcolata in prorata.`
    }
    return `Attivazione ${plan.name} per centro in prova gratuita.`
  }

  const price = selectedInterval === 'yearly' 
    ? plan.pricing?.yearly 
    : plan.pricing?.monthly

  const handleConfirm = () => {
    if (isUpgrade) {
      onUpgrade(selectedCenter, plan.id, selectedInterval)
    } else {
      onConfirm(selectedCenter, plan.id, selectedInterval, couponCode || undefined)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {isUpgrade ? `Cambia a ${plan.name}` : `Attiva Piano ${plan.name}`}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Seleziona il centro e l'intervallo di fatturazione
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Selezione Centro */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Seleziona centro
            </label>
            {allCenters.length === 0 ? (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                Nessun centro disponibile.
              </div>
            ) : (
              <div className="space-y-2">
                {allCenters.map(c => {
                  const isPendingDeletion = c.centerStatus === 'pending_deletion'
                  const isCurrent = c.plan === plan.id && c.status !== 'trialing' && !isPendingDeletion
                  const hasPaidPlan = c.plan !== 'trial' && c.status !== 'trialing' && !isPendingDeletion
                  const isDisabled = isCurrent || isPendingDeletion

                  return (
                    <button
                      key={c.centerId}
                      onClick={() => setSelectedCenter(c.centerId)}
                      disabled={isDisabled}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                        selectedCenter === c.centerId
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : isDisabled
                            ? 'border-gray-200 dark:border-gray-600 opacity-50 cursor-not-allowed'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-gray-900 dark:text-white text-sm">
                            {c.centerName}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isPendingDeletion ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                              In cancellazione
                            </span>
                          ) : hasPaidPlan ? (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              planColors[c.plan]?.replace('border-l-', 'bg-').replace('-400', '-100') || 'bg-gray-100'
                            } ${
                              c.plan === 'starter' ? 'text-green-700' :
                              c.plan === 'professional' ? 'text-purple-700' :
                              c.plan === 'enterprise' ? 'text-amber-700' : 'text-gray-700'
                            }`}>
                              {c.plan.charAt(0).toUpperCase() + c.plan.slice(1)}
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                              Trial
                            </span>
                          )}
                          {isCurrent && (
                            <span className="text-xs text-gray-400">Piano attuale</span>
                          )}
                        </div>
                      </div>
                      {isPendingDeletion && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1.5">
                          Annulla prima la cancellazione dalla card del centro.
                        </p>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Info azione */}
          {selectedCenter && (
            <div className={`p-3 rounded-lg text-sm ${
              isSamePlan 
                ? 'bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400'
                : isUpgrade
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
            }`}>
              <div className="flex items-center gap-2">
                {isUpgrade ? <TrendingUp className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
                <span className="font-medium">{getActionLabel()}</span>
              </div>
              <p className="mt-1">{getActionDescription()}</p>
            </div>
          )}

          {/* Selezione Intervallo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fatturazione
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSelectedInterval('monthly')}
                className={`p-3 rounded-lg border-2 text-center transition-colors ${
                  selectedInterval === 'monthly'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <p className="font-bold text-gray-900 dark:text-white">
                  {plan.pricing?.monthly?.formatted || '—'}/mese
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Mensile</p>
              </button>
              <button
                onClick={() => setSelectedInterval('yearly')}
                className={`p-3 rounded-lg border-2 text-center transition-colors ${
                  selectedInterval === 'yearly'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <p className="font-bold text-gray-900 dark:text-white">
                  {plan.pricing?.yearly?.monthlyEquivalent || '—'}/mese
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  Annuale ({plan.pricing?.yearly?.formatted || '—'}/anno)
                </p>
              </button>
            </div>
          </div>

          {/* Codice Coupon - solo per nuove attivazioni */}
          {!isUpgrade && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Codice promozionale <span className="text-gray-400 font-normal">(opzionale)</span>
              </label>
              <input
                type="text"
                value={couponCode}
                onChange={e => setCouponCode(e.target.value.toUpperCase())}
                placeholder="es. BETA2026"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}
        </div>

        {/* Riepilogo */}
        {selectedCenter && !isSamePlan && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Piano</span>
              <span className="font-medium text-gray-900 dark:text-white">{plan.name}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-600 dark:text-gray-400">Fatturazione</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {selectedInterval === 'yearly' ? 'Annuale' : 'Mensile'}
              </span>
            </div>
            <div className="flex justify-between text-sm mt-1 pt-2 border-t border-gray-200 dark:border-gray-600">
              <span className="font-medium text-gray-900 dark:text-white">Totale</span>
              <span className="font-bold text-primary-600 dark:text-primary-400">
                {selectedInterval === 'yearly' 
                  ? plan.pricing?.yearly?.formatted 
                  : plan.pricing?.monthly?.formatted
                }
                /{selectedInterval === 'yearly' ? 'anno' : 'mese'}
              </span>
            </div>
            {isUpgrade && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                La differenza di prezzo verrà calcolata automaticamente in prorata da Stripe.
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedCenter || isSamePlan || loading}
            className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isUpgrade ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
            {isUpgrade 
              ? (isDowngrade ? 'Conferma Downgrade' : 'Conferma Upgrade')
              : 'Procedi al Pagamento'
            }
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function OrganizationBilling() {
  const [loading, setLoading] = useState(true)
  const [portalLoading, setPortalLoading] = useState(false)
  const [error, setError] = useState(null)
  const [billing, setBilling] = useState(null)
  const [invoices, setInvoices] = useState([])
  // FIX: Stato per piani disponibili e betaMode
  const [plans, setPlans] = useState([])
  const [plansLoading, setPlansLoading] = useState(true)
  const [betaMode, setBetaMode] = useState(true)
  // Checkout
  const [checkoutPlan, setCheckoutPlan] = useState(null)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  // Cancel/Reactivate subscription
  const [cancelTargetSub, setCancelTargetSub] = useState(null)
  const [subActionLoading, setSubActionLoading] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const { showSuccess, showError } = useNotification()
  const { user } = useAuth()

  // Solo responsabile può gestire cancel/reactivate (backend usa requireResponsabile)
  const canManageSubscription = user?.role === 'responsabile' || user?.role === 'superadmin'
  
  // Gestisci ritorno da Stripe Checkout
  useEffect(() => {
    const checkoutResult = searchParams.get('checkout')
    if (checkoutResult === 'success') {
      showSuccess('Abbonamento attivato con successo! Il piano potrebbe impiegare qualche secondo per aggiornarsi.')
      setSearchParams({}, { replace: true })
    } else if (checkoutResult === 'cancel') {
      showError('Pagamento annullato. Puoi riprovare quando vuoi.')
      setSearchParams({}, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  
  const loadBilling = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // API per caricare i dati di billing
      const res = await organizationApi.getBilling()
      setBilling(res.data.billing || res.data.data?.billing || null)
      
      // FATTURAZIONE: Carica fatture dall'endpoint organization
      // Usa /organization/invoices che aggrega fatture Stripe di tutti i centri dell'org
      try {
        const invRes = await api.get('/organization/invoices')
        const orgInvoices = invRes.data?.data?.invoices || invRes.data?.invoices || []
        setInvoices(orgInvoices)
      } catch (invErr) {
        console.warn('Organization invoices API not available:', invErr.message)
        setInvoices([])
      }
    } catch (err) {
      console.error('Error loading billing:', err)
      if (err.response?.status === 404) {
        setBilling(null)
        setInvoices([])
      } else {
        setError(err.response?.data?.message || 'Errore nel caricamento dei dati di fatturazione')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  // FIX: Carica piani disponibili
  const loadPlans = useCallback(async () => {
    try {
      setPlansLoading(true)
      const res = await organizationApi.getPlans()
      const plansData = res.data?.data?.plans || res.data?.plans || []
      setPlans(plansData)
      setBetaMode(res.data?.data?.betaMode ?? true)
    } catch (err) {
      console.error('Error loading plans:', err)
      setPlans([])
    } finally {
      setPlansLoading(false)
    }
  }, [])
  
  useEffect(() => {
    loadBilling()
    loadPlans()
  }, [loadBilling, loadPlans])
  
  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true)
      
      // Apri Stripe Customer Portal
      const res = await organizationApi.createBillingPortalSession()
      
      if (res.data.data?.portalUrl || res.data.data?.url) {
        window.location.href = res.data.data.portalUrl || res.data.data.url
      } else {
        showError('Impossibile aprire il portale di fatturazione')
      }
    } catch (err) {
      console.error('Error opening billing portal:', err)
      showError(err.response?.data?.message || 'Errore nell\'apertura del portale')
    } finally {
      setPortalLoading(false)
    }
  }
  
  const handleCheckout = async (centerId, plan, interval, couponCode) => {
    try {
      setCheckoutLoading(true)
      
      const res = await organizationApi.createCheckoutSession(centerId, plan, interval, couponCode)
      
      const checkoutUrl = res.data.data?.checkoutUrl || res.data.checkoutUrl
      if (checkoutUrl) {
        window.location.href = checkoutUrl
      } else {
        showError('Impossibile avviare il pagamento. Riprova.')
      }
    } catch (err) {
      console.error('Error creating checkout session:', err)
      const msg = err.response?.data?.message || 'Errore nell\'avvio del pagamento'
      showError(msg)
    } finally {
      setCheckoutLoading(false)
    }
  }

  // === Cancel / Reactivate Subscription (per-centro) ===
  const handleCancelSubscription = async (sub, reason) => {
    if (!sub?.centerId) return
    try {
      setSubActionLoading(true)
      const res = await organizationApi.cancelCenterSubscription(sub.centerId, reason)
      const activeUntil = res.data?.data?.activeUntil
      const activeUntilStr = activeUntil
        ? new Date(activeUntil).toLocaleDateString('it-IT')
        : null
      showSuccess(
        activeUntilStr
          ? `Abbonamento interrotto. Resterà attivo fino al ${activeUntilStr}.`
          : 'Abbonamento interrotto.'
      )
      setCancelTargetSub(null)
      await loadBilling()
    } catch (err) {
      console.error('Error canceling subscription:', err)
      showError(err.response?.data?.message || 'Errore nell\'interruzione dell\'abbonamento')
    } finally {
      setSubActionLoading(false)
    }
  }

  const handleReactivateSubscription = async (sub) => {
    if (!sub?.centerId) return
    const confirmed = window.confirm(
      `Riattivare l'abbonamento per ${sub.centerName}? Continuerà a rinnovarsi regolarmente.`
    )
    if (!confirmed) return
    try {
      setSubActionLoading(true)
      await organizationApi.reactivateCenterSubscription(sub.centerId)
      showSuccess('Abbonamento riattivato. Continuerà a rinnovarsi regolarmente.')
      await loadBilling()
    } catch (err) {
      console.error('Error reactivating subscription:', err)
      showError(err.response?.data?.message || 'Errore nella riattivazione dell\'abbonamento')
    } finally {
      setSubActionLoading(false)
    }
  }

  const handleCancelDeletion = async (sub) => {
    if (!sub?.centerId) return
    const confirmed = window.confirm(
      `Annullare la cancellazione del centro ${sub.centerName}?\n\nIl centro tornerà attivo e potrai poi riattivare un piano dalla sezione "Piani Disponibili".`
    )
    if (!confirmed) return
    try {
      setSubActionLoading(true)
      await organizationApi.cancelCenterDeletion(sub.centerId)
      showSuccess('Cancellazione annullata. Ora puoi riattivare un piano per il centro.')
      await loadBilling()
    } catch (err) {
      console.error('Error canceling deletion:', err)
      showError(err.response?.data?.message || 'Errore nell\'annullamento della cancellazione')
    } finally {
      setSubActionLoading(false)
    }
  }

  const handleUpgrade = async (centerId, plan, interval) => {
    try {
      setCheckoutLoading(true)
      
      const res = await organizationApi.upgradeCenterPlan(centerId, plan, interval)
      
      showSuccess(res.data.message || `Piano aggiornato a ${plan} con successo!`)
      setCheckoutPlan(null)
      
      // Ricarica dati billing per aggiornare la vista
      loadBilling()
      loadPlans()
    } catch (err) {
      console.error('Error upgrading plan:', err)
      const msg = err.response?.data?.message || 'Errore nel cambio piano'
      showError(msg)
    } finally {
      setCheckoutLoading(false)
    }
  }
  
  if (loading) {
    return <PageLoader />
  }
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
            Errore nel caricamento
          </h2>
          <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
          <button onClick={loadBilling} className="btn-primary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Riprova
          </button>
        </div>
      </div>
    )
  }
  
  return (
    <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/organization"
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Fatturazione
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gestisci il tuo piano e visualizza le fatture
          </p>
        </div>
      </div>
      
      {/* Riepilogo Abbonamenti */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary-600" />
          Riepilogo
        </h2>
        <BillingSummaryCard 
          billing={billing} 
          onManage={handleManageSubscription}
          loading={portalLoading}
        />
      </section>
      
      {/* Abbonamenti per Centro */}
      {billing?.subscriptions?.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary-600" />
            Abbonamenti per Centro
          </h2>
          <div className="space-y-4">
            {billing.subscriptions.map(sub => (
              <CenterSubscriptionCard
                key={sub.centerId}
                sub={sub}
                canManage={canManageSubscription}
                onCancel={(s) => setCancelTargetSub(s)}
                onReactivate={handleReactivateSubscription}
                onCancelDeletion={handleCancelDeletion}
                actionLoading={subActionLoading}
              />
            ))}
          </div>
        </section>
      )}

      {/* FIX: Piani Disponibili - NUOVA SEZIONE */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary-600" />
          Piani Disponibili
        </h2>

        {/* Info Beta Tester */}
        {betaMode && (
          <Card className="mb-6 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border-primary-200 dark:border-primary-800">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-primary-800 dark:text-primary-200 font-medium">
                  🎉 Grazie per essere un Beta Tester!
                </p>
                <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                  Durante la fase beta, hai accesso completo a tutte le funzionalità gratuitamente. 
                  I prezzi mostrati entreranno in vigore al termine del periodo beta.
                </p>
              </div>
            </div>
          </Card>
        )}
        
        {plansLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : plans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans
              .filter(plan => plan.id !== 'trial')
              .map(plan => (
                <AvailablePlanCard 
                  key={plan.id} 
                  plan={plan} 
                  betaMode={betaMode}
                  onSelect={setCheckoutPlan}
                />
              ))}
          </div>
        ) : (
          <Card className="text-center py-8">
            <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Piani non disponibili al momento
            </p>
            <button onClick={loadPlans} className="btn-secondary flex items-center gap-2 mx-auto">
              <RefreshCw className="h-4 w-4" />
              Ricarica
            </button>
          </Card>
        )}
      </section>
      
      {/* Invoices */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Receipt className="h-5 w-5 text-primary-600" />
          Storico Fatture
        </h2>
        
        {invoices.length === 0 ? (
          <Card className="text-center py-8">
            <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Nessuna fattura
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Le fatture appariranno qui dopo il primo pagamento
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {invoices.map(invoice => (
              <InvoiceRow 
                key={invoice.id || invoice._id} 
                invoice={invoice}
                onResend={(type, msg) => type === 'success' ? showSuccess(msg) : showError(msg)}
              />
            ))}
          </div>
        )}
      </section>
      
      {/* Payment Methods Info */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800 dark:text-blue-200">
              Metodi di pagamento
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Per aggiungere o modificare i metodi di pagamento, clicca su "Gestisci Abbonamento" 
              per accedere al portale Stripe sicuro.
            </p>
          </div>
        </div>
      </Card>
      
      {/* Checkout/Upgrade Modal */}
      {checkoutPlan && (
        <CheckoutModal
          plan={checkoutPlan}
          centers={billing?.subscriptions || []}
          onConfirm={handleCheckout}
          onUpgrade={handleUpgrade}
          onClose={() => setCheckoutPlan(null)}
          loading={checkoutLoading}
        />
      )}

      {/* Cancel Subscription Modal */}
      {cancelTargetSub && (
        <CancelSubscriptionModal
          sub={cancelTargetSub}
          onConfirm={handleCancelSubscription}
          onClose={() => setCancelTargetSub(null)}
          loading={subActionLoading}
        />
      )}
    </div>
  )
}
